import React, { useEffect, useState } from 'react';
import instance from '../../service/axiosInstane';
import MuiTable from '../../components/MuiTable/MuiTable';
import PageHeading from '../../components/PageHeading/PageHeading';
import SearchInput from '../../components/SearchInput/SearchInput';
import Button from '../../components/Button/Button';
import {
  updateFlashBar,
  updateIsLoading,
  update_breadcrumb,
} from '../../redux/action';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import InputForm from '../../components/InputForm/InputForm';
import Input from '../../components/Input/Input';
import { flashBarTheme } from '../../config/flashBar';
import { errorMessage } from '../../config/errorsMsg';
import { Role, SiteMapping, SiteStatus, SortOrderType } from '../../config/enum';
import { errorMessageAutoCapitalize, totalPages } from '../../config/helpers';
import { StateProps } from '../../redux/reducer';
import Suggestion from '../../components/Suggestion/Suggestion';
import { Column } from '../../components/MuiTable/ViewData';
import { successMessage } from '../../config/keyword';

const columns: Column[] = [
  {
    id: 'siteSequenceNumber',
    label: 'Site Number',
    minWidth: 120,
    align: 'center',
  },
  {
    id: 'host',
    label: 'Host',
    minWidth: 120,
    align: 'left',
  },
  {
    id: 'name',
    label: 'Name',
    minWidth: 180,
    align: 'left',
  },
  {
    id: 'siteStatus',
    label: 'Site Status',
    minWidth: 120,
    align: 'left',
  },
  {
    id: 'timeStamp',
    label: 'Time Stamp',
    minWidth: 120,
    align: 'left',
  },
  {
    id: 'action',
    label: 'Action',
    minWidth: 0,
    align: 'right',
  },
];

type ResultProps = {
  siteOrganizationId: string;
};

type RequestProps = {
  Page: number;
  PageSize: number;
  SearchText: string;
  SortBy: string;
  SortOrder: string | number;
};

type StateProp = {
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  results?: ResultsProps[];
};

type ResultsProps = {
  host: string;
  name: string;
  organizationId: string;
  siteId: string;
  siteOrganizationId: string;
  siteSequenceNumber: number;
  siteUrl: string;
  siteStatus?: number;
  timeStamp?: string;
};

const WebsitesAdmin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userInfo = useSelector((state: StateProps) => state.tokenInfo.userInfo);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [state, setState] = useState<ResultsProps[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [formError, setFormError] = useState<any>({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [searchSuggestion, setSearchedSuggetion] = useState([]);
  const [websites, setWebsites] = useState<RequestProps>({
    Page: page,
    PageSize: limit,
    SearchText: '',
    SortBy: 'siteUrl',
    SortOrder: 0,
  });
  const [addWebsite, setAddWebsite] = useState<{ siteUrl: string; name: string }>({
    siteUrl: '',
    name: '',
  });
  const { siteUrl, name } = addWebsite;

  useEffect(() => {
    const breadcrumb = [{ path: '/websites', name: 'Website' }];
    dispatch(update_breadcrumb(breadcrumb));
    getWebsites(websites);
  }, []);

  useEffect(() => {
    if (Object.keys(formError).length === 0 && isSubmit) {
      onAddWebsite();
    }
  }, [formError]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (addWebsite.siteUrl !== '') {
        searchWebsite();
      } else {
        setSearchedSuggetion([]);
      }
    }, 1000);
    return () => {
      clearTimeout(handler);
    };
  }, [addWebsite.siteUrl]);

  const validation = () => {
    const error: any = {};
    if (!siteUrl) {
      error.siteUrl = errorMessage.emptyWebsite;
    }
    if (!name) {
      error.name = errorMessage.emptyName;
    } else if (name.length > 50) {
      error.name = errorMessage.siteValidLengthName;
    }
    return error;
  };

  const searchWebsite = async () => {
    await instance
      .get(`/site-organizations/unmapped-sites`, {
        params: { searchText: siteUrl, organizationId: userInfo.organizationId },
      })
      .then((response) => {
        if (response.status === 200) {
          setSearchedSuggetion(response.data);
        }
      })
      .catch(() => {
        const obj = {
          isOpen: true,
          message: errorMessage.oops,
          type: flashBarTheme.ERROR,
        };
        dispatch(updateFlashBar(obj));
      });
  };

  const gotResponse = (data: StateProp) => {
    if (data?.results !== null) {
      setState(data?.results as ResultsProps[]);
      setTotalRowCount(data.totalCount);
    } else {
      setState([]);
    }
    setIsSubmit(false);
    dispatch(updateIsLoading(false));
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.name === 'siteUrl') {
      const format = /^\S*$/;
      if (!format.test(event.target.value)) {
        return;
      }
    }
    setAddWebsite({
      ...addWebsite,
      [event.target.name]: event.target.value,
    });

    if (event.target.value !== '') {
      setFormError({ ...formError, [event.target.name]: undefined });
    } else {
      setFormError({
        ...formError,
        [event.target.name]: `${errorMessageAutoCapitalize(
          event.target.name
        )} is required`,
      });
    }
  };

  const onSubmit = () => {
    setFormError(validation());
    setIsSubmit(true);
  };

  const searchHandler = () => {
    getWebsites({
      ...websites,
      SearchText: websites.SearchText,
      PageSize: limit,
    });
  };
  const handleEnterKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      searchHandler();
    }
  };

  const onSearchHanlder = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWebsites({ ...websites, SearchText: e.target.value, Page: 1 });
    setPage(1);
  };

  const sorting = (sortOrder: string) => {
    let temp = '';
    if (sortOrder == SortOrderType.DESC) {
      temp = SortOrderType.ASC;
    } else {
      temp = SortOrderType.DESC;
    }
    setWebsites({ ...websites, SortOrder: temp });
    return temp;
  };

  const sortRecord = (fieldName: string, sortOrder: string) => {
    getWebsites({
      ...websites,
      Page: page,
      PageSize: limit,
      SearchText: websites.SearchText,
      SortBy: fieldName,
      SortOrder: websites.SortOrder,
    });
    setWebsites({
      ...websites,
      SortBy: fieldName,
      SortOrder: sorting(sortOrder),
    });
  };

  const paginateHandler = {
    onClickPageNumber: (page: number) => {
      getWebsites({
        ...websites,
        Page: page,
        PageSize: limit,
        SortBy: websites.SortBy,
        SortOrder: websites.SortOrder,
      });
      setPage(page);
    },
    onNextClick: () => {
      if (page < totalPages(totalRowCount, limit)) {
        getWebsites({
          ...websites,
          Page: page + 1,
          PageSize: limit,
          SortBy: websites.SortBy,
          SortOrder: websites.SortOrder,
        });
        setPage(page + 1);
      }
    },
    onPrevClick: () => {
      if (page > 1) {
        getWebsites({
          ...websites,
          Page: page - 1,
          PageSize: limit,
          SortBy: websites.SortBy,
          SortOrder: websites.SortOrder,
        });
        setPage(page - 1);
      }
    },
    onFirstClick: () => {
      if (page !== 1) {
        getWebsites({
          ...websites,
          Page: 1,
          PageSize: limit,
          SortBy: websites.SortBy,
          SortOrder: websites.SortOrder,
        });
        setPage(1);
      }
    },
    onLastClick: () => {
      if (page !== totalPages(totalRowCount, limit)) {
        getWebsites({
          ...websites,
          Page: totalPages(totalRowCount, limit),
          PageSize: limit,
          SortBy: websites.SortBy,
          SortOrder: websites.SortOrder,
        });
        setPage(totalPages(totalRowCount, limit));
      }
    },

    limitHandler: (item: number) => {
      getWebsites({
        ...websites,
        Page: 1,
        PageSize: item,
        SortBy: websites.SortBy,
        SortOrder: websites.SortOrder,
      });
      setLimit(item);
      setPage(1);
    },
  };

  const onAddWebsite = async () => {
    const obj = {
      siteUrl: addWebsite.siteUrl.toLowerCase(),
      name: addWebsite.name,
      organizationId: userInfo.organizationId,
    };
    dispatch(updateIsLoading(true));
    await instance
      .post('/site', obj)
      .then((response) => {
        if (response.status === 201) {
          setIsFormOpen(false);
          const obj = {
            isOpen: true,
            message: successMessage.SITE_ADD,
            type: flashBarTheme.SUCCESS,
          };
          dispatch(updateFlashBar(obj));
          getWebsites({ ...websites, Page: page, PageSize: limit });
          setAddWebsite({
            ...addWebsite,
            siteUrl: '',
            name: '',
          });
          dispatch(updateIsLoading(false));
        }
      })
      .catch((err) => {
        setIsSubmit(false);
        const obj = {
          isOpen: true,
          message: err.response.data.Message
            ? err.response.data.Message
            : errorMessage.oops,
          type: flashBarTheme.ERROR,
        };
        dispatch(updateFlashBar(obj));
        dispatch(updateIsLoading(false));
      });
  };

  const onClearSearch = () => {
    setWebsites({
      ...websites,
      SearchText: '',
      Page: page,
      PageSize: limit,
    });
    getWebsites({
      ...websites,
      SearchText: '',
      Page: page,
      PageSize: limit,
    });
  };

  const onCancel = () => {
    setIsFormOpen(false);
    setIsSubmit(false);
    setFormError({});
    setAddWebsite({
      ...addWebsite,
      siteUrl: '',
      name: '',
    });
  };

  const viewRecord = (row: ResultProps) => {
    navigate(`/websiteDetail/${row.siteOrganizationId}`);
  };

  const getWebsites = async (obj: RequestProps) => {
    dispatch(updateIsLoading(true));
    await instance
      .get('/site-organizations', {
        params: obj,
      })
      .then((response) => {
        if (response.status === 200) {
          gotResponse(response.data);
        }
      })
      .catch(() => {
        const obj = {
          isOpen: true,
          message: errorMessage.oops,
          type: errorMessage.oops,
        };
        dispatch(updateFlashBar(obj));
        dispatch(updateIsLoading(false));
      });
  };

  const onUnmappedWebsite = async (arg: RequestProps) => {
    setIsSubmit(false);
    const obj = {
      siteId: arg,
      organizationId: userInfo.organizationId,
    };
    dispatch(updateIsLoading(true));
    await instance
      .post('/site-organizations', obj)
      .then((response) => {
        if (response.status === 201) {
          setIsFormOpen(false);
          const obj = {
            isOpen: true,
            message: successMessage.SITE_ADD,
            type: flashBarTheme.SUCCESS,
          };
          dispatch(updateFlashBar(obj));
          getWebsites({ ...websites, Page: page, PageSize: limit });
          setFormError({});
          setAddWebsite({
            ...addWebsite,
            siteUrl: '',
            name: '',
          });
          dispatch(updateIsLoading(false));
        }
      })
      .catch((err) => {
        const obj = {
          isOpen: true,
          message: err.response.data.Message
            ? err.response.data.Message
            : errorMessage.oops,
          type: flashBarTheme.ERROR,
        };
        dispatch(updateFlashBar(obj));

        dispatch(updateIsLoading(false));
      });
  };

  return (
    <div className="px-2 py-1 ">
      <PageHeading label="Websites Management" />
      <div className="bg-white p-2 rounded-sm shadow-lg">
        <div className="md:w-full md:flex items-center justify-between mb-2 transition-all duration-200 ease-in-out">
          <SearchInput
            value={websites.SearchText}
            onChange={onSearchHanlder}
            onSearch={searchHandler}
            onClear={() => onClearSearch()}
            handleEnterKeyPress={handleEnterKeyPress}
          />
          {userInfo.role !== Role.User && (
            <div className="mt-2 sm:mt-0">
              <Button
                label="Add"
                type="button"
                buttonType="primary"
                icon={<i className="fa-solid fa-plus"></i>}
                onClick={() => setIsFormOpen(!isFormOpen)}
              />
            </div>
          )}
        </div>
        {isFormOpen && (
          <InputForm
            title={'Add New Website'}
            isFormOpen={isFormOpen}
            onSubmit={onSubmit}
            onCancel={onCancel}
          >
            <div className="lg:flex items-center justify-between">
              <div className="relative">
                <div>
                  <Input
                    name={'siteUrl'}
                    type={'text'}
                    label={'Site Url'}
                    value={siteUrl}
                    placeholder={`Enter Site Url`}
                    onChange={handleChange}
                    error={formError?.siteUrl}
                  />
                </div>
                <Suggestion
                  state={searchSuggestion}
                  onClickHandler={onUnmappedWebsite}
                />
              </div>
              <Input
                name={'name'}
                type={'text'}
                value={name}
                label={'Name'}
                placeholder={`Enter Name`}
                onChange={handleChange}
                error={formError?.name}
              />
            </div>
          </InputForm>
        )}

        <MuiTable
          columns={columns}
          rows={state.map((item: ResultsProps) => ({
            ...item,
            siteStatus: SiteMapping[item?.siteStatus as SiteStatus],
          }))}
          page={page}
          limit={limit}
          totalRowCount={totalRowCount}
          onSortHandler={sortRecord}
          sortOrder={websites.SortOrder}
          checkBoxShow={false}
          checkBoxValue={'id'}
          onViewHandler={viewRecord}
          paginateHandler={paginateHandler}
          showView={true}
          sortArray={['name', 'host', 'siteSequenceNumber', 'timeStamp']}
        />
      </div>
    </div>
  );
};

export default WebsitesAdmin;
